<template>
  <div class="page-container">
    <el-card>
      <!-- 搜索区 -->
      <div class="search-bar">
        <el-input
          v-model="query.keyword"
          placeholder="搜索产品号/名称/略称"
          clearable
          style="width: 220px"
          @keyup.enter.native="handleSearch"
        />
        <el-select v-model="query.status" placeholder="产品状态" clearable style="width: 120px">
          <el-option label="上架" :value="1" />
          <el-option label="下架" :value="0" />
        </el-select>
        <el-button type="primary" size="mini" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <el-button size="mini" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <!-- 操作按钮 -->
      <div class="action-bar">
        <el-button
          v-permission="['product:create']"
          type="success"
          size="mini"
          icon="el-icon-plus"
          @click="handleCreate"
        >新增产品</el-button>
      </div>

      <!-- 表格 -->
      <el-table :data="tableData" v-loading="loading" border stripe style="width: 100%">
        <el-table-column prop="id" label="ID" width="65" align="center" />
        <el-table-column label="缩略图" width="80" align="center">
          <template slot-scope="{ row }">
            <el-image
              v-if="row.thumbnail"
              :src="row.thumbnail"
              :preview-src-list="[row.thumbnail]"
              fit="cover"
              style="width:48px;height:48px;border-radius:4px;display:block;margin:0 auto"
            />
            <span v-else class="text-muted">暂无</span>
          </template>
        </el-table-column>
        <el-table-column prop="product_no" label="产品号" width="140" />
        <el-table-column prop="name" label="产品名称" min-width="150" show-overflow-tooltip />
        <el-table-column prop="short_name" label="产品略称" width="120" show-overflow-tooltip />
        <el-table-column prop="selling_points" label="卖点" width="120" show-overflow-tooltip />
        <el-table-column label="价格" width="160" align="right">
          <template slot-scope="{ row }">
            <span class="price-real">¥{{ Number(row.price).toFixed(2) }}</span>
            <span v-if="row.original_price" class="price-original">¥{{ Number(row.original_price).toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="采样类型" width="140" align="center">
          <template slot-scope="{ row }">
            <template v-if="row.sample_types && row.sample_types.length">
              <el-tag
                v-for="t in row.sample_types"
                :key="t"
                size="mini"
                :type="t === 'blood' ? 'danger' : 'warning'"
                style="margin:2px"
              >{{ t === 'blood' ? '血液' : '组织' }}</el-tag>
            </template>
            <span v-else class="text-muted">—</span>
          </template>
        </el-table-column>
        <el-table-column prop="sort" label="排序" width="70" align="center" />
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'info'" size="small">
              {{ row.status === 1 ? '上架' : '下架' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="160" />
        <el-table-column label="操作" width="220" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              v-permission="['product:detail']"
              type="info" size="mini" icon="el-icon-view"
              @click="handleDetail(row)"
            >详情</el-button>
            <el-button
              v-permission="['product:update']"
              type="primary" size="mini" icon="el-icon-edit"
              @click="handleEdit(row)"
            >编辑</el-button>
            <el-button
              v-permission="['product:toggleStatus']"
              :type="row.status === 1 ? 'warning' : 'success'"
              size="mini"
              @click="handleToggleStatus(row)"
            >{{ row.status === 1 ? '下架' : '上架' }}</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        :current-page="query.page"
        :page-sizes="[10, 15, 20, 50]"
        :page-size="query.pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top: 16px; text-align: right"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </el-card>
  </div>
</template>

<script>
import { getProductList, toggleProductStatus } from '@/api/product'

export default {
  name: 'ProductManage',
  data() {
    return {
      loading: false,
      tableData: [],
      total: 0,
      query: { page: 1, pageSize: 15, keyword: '', status: '' }
    }
  },
  created() {
    this.fetchData()
  },
  activated() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const params = { ...this.query }
        if (params.status === '') delete params.status
        const res = await getProductList(params)
        const d = res.data || {}
        this.tableData = d.data || []
        this.total = d.total || 0
      } finally {
        this.loading = false
      }
    },

    handleSearch() { this.query.page = 1; this.fetchData() },
    handleReset() {
      this.query = { page: 1, pageSize: 15, keyword: '', status: '' }
      this.fetchData()
    },
    handleSizeChange(val) { this.query.pageSize = val; this.fetchData() },
    handleCurrentChange(val) { this.query.page = val; this.fetchData() },

    handleCreate() {
      this.$router.push('/product/edit')
    },

    handleEdit(row) {
      this.$router.push(`/product/edit?id=${row.id}`)
    },

    handleDetail(row) {
      this.$router.push(`/product/detail?id=${row.id}`)
    },

    async handleToggleStatus(row) {
      const action = row.status === 1 ? '下架' : '上架'
      try {
        await this.$confirm(`确认将产品「${row.name}」${action}？`, '提示', { type: 'warning' })
        await toggleProductStatus({ id: row.id })
        this.$message.success(`${action}成功`)
        this.fetchData()
      } catch {}
    }
  }
}
</script>

<style lang="scss" scoped>
.search-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.action-bar {
  margin-bottom: 14px;
}

.text-muted {
  color: #c0c4cc;
  font-size: 12px;
}

.price-real {
  color: #f56c6c;
  font-weight: 600;
  margin-right: 4px;
}

.price-original {
  color: #c0c4cc;
  font-size: 12px;
  text-decoration: line-through;
}
</style>
